import React, { useEffect, useState } from 'react';
import { Container } from '../components/common/Container';
import { Button } from '../components/common/Button';
import { GoogleMapEmbed, isMapConfigured } from '../components/common/GoogleMapEmbed';
import { Icon } from '../lib/icons';
import { dataService } from '../services/dataService';
import { useLanguage } from '../context/LanguageContext';

export function InfoBnb() {
  const [data, setData] = useState(null);
  const { config, t, lang } = useLanguage();
  const mapCfg = config?.tenant?.settings?.map;
  const address = config?.tenant?.settings?.contact?.address;
  useEffect(() => { dataService.getInfoBnb().then(setData); }, [lang]);
  if (!data) return null;

  return (
    <div className="pb-20 pt-8">
      {/* HERO */}
      <Container>
        <section className="grid grid-cols-1 items-stretch overflow-hidden rounded-card border border-paelbi-line bg-paelbi-bg2/50 md:grid-cols-2">
          <div className="flex flex-col justify-center p-8 md:p-12">
            <h1 className="font-display text-3xl leading-[1.15] text-paelbi-ink md:text-[40px]">
              {data.hero.title.map((l, i) => <span key={`h-${i}`} className="block">{l}</span>)}
            </h1>
            <p className="mt-5 max-w-md font-sans text-base leading-relaxed text-paelbi-ink/75">{data.hero.text}</p>
          </div>
          <div className="relative min-h-[240px] md:min-h-0">
            <img src={data.hero.image} alt="La tua camera" className="absolute inset-0 h-full w-full object-cover object-center" />
          </div>
        </section>
      </Container>

      {/* INFO CARDS */}
      <Container className="mt-12">
        <h2 className="font-display text-2xl text-paelbi-ink md:text-[28px]">{t('Informazioni utili')}</h2>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {data.infoCards.map((c) => (
            <div key={c.title} className="rounded-card border border-paelbi-line bg-white p-6 shadow-card transition-all hover:-translate-y-1 hover:shadow-soft">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-paelbi-bg2"><Icon name={c.icon} size={20} className="text-paelbi-brown" /></span>
              <h3 className="mt-4 font-display text-lg text-paelbi-ink">{c.title}</h3>
              <ul className="mt-3 space-y-1.5">
                {c.lines.map((l, i) => (
                  <li key={`${c.title}-${i}`} className="font-sans text-sm leading-relaxed text-paelbi-ink/70">{l}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Container>

      {/* AMENITIES */}
      <Container className="mt-10">
        <div className="rounded-card border border-paelbi-line bg-white p-8 shadow-card">
          <h3 className="font-sans text-xs font-semibold uppercase tracking-[0.08em] text-paelbi-ink/60">Servizi inclusi</h3>
          <div className="mt-6 grid grid-cols-2 gap-6 sm:grid-cols-4 lg:grid-cols-7">
            {data.amenities.map((a) => (
              <div key={a.label} className="flex flex-col items-center gap-2 text-center">
                <Icon name={a.icon} size={28} strokeWidth={1.5} className="text-paelbi-brown" />
                <span className="font-sans text-xs text-paelbi-ink/70">{a.label}</span>
              </div>
            ))}
          </div>
        </div>
      </Container>

      {/* NUMBERS */}
      <Container className="mt-12">
        <h2 className="font-display text-2xl text-paelbi-ink md:text-[28px]">Numeri utili</h2>
        <div className="mt-6 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {data.numbers.map((n) => (
            <div key={n.title}>
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-paelbi-bg2"><Icon name={n.icon} size={18} className="text-paelbi-brown" /></span>
              <h3 className="mt-3 font-display text-base text-paelbi-ink">{n.title}</h3>
              <div className="mt-3 space-y-2 font-sans text-sm text-paelbi-ink/70">
                {n.lines && n.lines.map((l, i) => <p key={`${n.title}-l-${i}`}>{l}</p>)}
                {n.rows && n.rows.map((r, i) => (
                  <p key={`${n.title}-r-${i}`}><span className="font-bold text-paelbi-ink">{r[0]}</span> {r[1]}</p>
                ))}
                {n.blocks && n.blocks.map((b, i) => (
                  <div key={`${n.title}-b-${i}`} className="leading-relaxed">
                    <p className="font-bold text-paelbi-ink">{b[0]}</p>
                    <p>{b[1]}</p>
                    <p className="text-paelbi-ink/60">{b[2]}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Container>

      {/* DOVE SIAMO (live map) */}
      {isMapConfigured(mapCfg) && (
        <Container className="mt-12">
          <h2 className="font-display text-2xl text-paelbi-ink md:text-[28px]">{t('Dove siamo')}</h2>
          {address && (
            <p className="mt-2 flex items-center gap-1.5 font-sans text-sm text-paelbi-ink/70">
              <Icon name="mapPin" size={15} className="text-paelbi-brown" />{address}
            </p>
          )}
          <div className="mt-6">
            <GoogleMapEmbed map={mapCfg} title={t('Posizione della struttura')} height={360} />
          </div>
        </Container>
      )}

      {/* THANKS */}
      <Container className="mt-12">
        <div className="grid grid-cols-1 items-stretch overflow-hidden rounded-card bg-paelbi-bg2/60 md:grid-cols-2">
          <div className="flex flex-col justify-center p-8 md:p-10">
            <h3 className="font-display text-2xl text-paelbi-ink">{data.thanks.title}</h3>
            <p className="mt-3 max-w-md font-sans text-base leading-relaxed text-paelbi-ink/75">{data.thanks.text}</p>
          </div>
          <div className="relative min-h-[200px]">
            <img src={data.thanks.image} alt="Relax" loading="lazy" className="absolute inset-0 h-full w-full object-cover object-center" />
          </div>
        </div>
      </Container>
    </div>
  );
}

export default InfoBnb;
